import NQSRequest from './NQSRequest';
import { AnalyticsTag } from '../../common/Constants';
import Constants from './Services';
import Log from '../../common/log';
import RequestQueue from '../net/RequestQueue';
/**
 * Services that will be automatically retried on failure.
 * These services use network-layer retry with the following strategy:
 * - First 10 attempts: Fixed 5-second intervals
 * - Attempts 11-25: Exponential backoff (capped at 2 minutes)
 * - Retry continues even after service destruction
 * - Max 25 attempts before giving up
 *
 * Includes:
 * - Video analytics critical events (/start, /init, /join, /stop, /error, /adError)
 * - Session critical events (/infinity/session/start, /stop, /event, /nav)
 */
const retryServices = [
    // Video analytics critical events
    Constants.Service.START,
    Constants.Service.INIT,
    Constants.Service.JOIN,
    Constants.Service.STOP,
    Constants.Service.ERROR,
    Constants.Service.AD_ERROR,
    // Session critical events (using network-layer retry)
    Constants.Service.SESSION_START,
    Constants.Service.SESSION_STOP,
    Constants.Service.EVENT,
    Constants.Service.NAV
];
const MAX_RETRY_REQUESTS = 25;
export default class NQSAnalyticsService {
    constructor(options, coreStorage, fastDataService) {
        this.databaseSendingInterval = 2 * 60 * 1000;
        this.isDestroyed = false;
        this.servicesToWait = [Constants.Service.INIT, Constants.Service.START];
        this.sentStart = false;
        this.options = options;
        this.coreStorage = coreStorage;
        this.fastDataService = fastDataService;
        // Initialize centralized request queue with current retry policy
        this.requestQueue = new RequestQueue({
            retryServices,
            maxRetryRequests: MAX_RETRY_REQUESTS,
            onSuccess: (request) => {
                if (this.isDestroyed)
                    return;
                // Mark sentStart if applicable
                if (this.servicesToWait.includes(request.getService())) {
                    this.sentStart = true;
                }
                // Update last sent time and dataTime
                this.timeOfLastSentRequest = new Date().getTime();
                this.coreStorage.setDataTime(this.timeOfLastSentRequest.toString());
            },
            onFail: (_request) => {
                // Nothing extra – retry policy handled by queue
            }
        });
    }
    destroy() {
        // Set flag first to prevent new operations
        this.isDestroyed = true;
        // Abort and clear queued work
        this.requestQueue.destroy();
        this.timeWhenLastSentFromDatabase = new Date().getTime();
    }
    send(service, method, params, onSuccessCallback, onFailCallback, expirationManager) {
        var _a;
        if (this.isDestroyed)
            return;
        // Check if this service will be retried by network layer
        const isRetryable = retryServices.includes(service);
        // For retryable services: don't pass application callbacks to NQSRequest
        // They will be called on EVERY retry attempt, causing queue corruption
        // Instead, pass null and let RequestQueue manage final outcomes
        const request = new NQSRequest((_a = this.options['app.https']) !== null && _a !== void 0 ? _a : true, this.coreStorage.getHost(), service, method, params !== null && params !== void 0 ? params : {}, isRetryable ? undefined : onSuccessCallback, // Only pass callbacks for non-retryable
        isRetryable ? undefined : onFailCallback, expirationManager);
        // Store application callbacks on the request for RequestQueue to invoke on final outcomes
        if (isRetryable && (onSuccessCallback || onFailCallback)) {
            request._appSuccessCallback = onSuccessCallback;
            request._appFailCallback = onFailCallback;
        }
        // Check for CDN using the Constants.Service.CDN value directly
        const isCDN = service === Constants.Service.CDN;
        if (typeof this.options.authToken !== 'undefined' && typeof this.options.authType !== 'undefined' && !isCDN) {
            Log.verbose(AnalyticsTag, 'Adding Authorization header with type ' + this.options.authType);
            request.addHeader('Authorization', this.options.authType + ' ' + this.options.authToken);
        }
        this.requestQueue.enqueue(request);
    }
    getTimeOfLastSentRequest() {
        return this.timeOfLastSentRequest;
    }
    wasStartSent() {
        return this.sentStart;
    }
}
